import { useCallback, useEffect, useMemo } from 'react';

import { useLocalStorage } from '../useLocalStorage';
import { setGlobalCopyToastOptions } from 'src/slices/ui/uiSlice';
import { useAppDispatch } from 'src/store/hooks';

interface CopyPasteState {
  copyFromEntryStepId: string;
  copyPasteKey: string;
  copyPasteKeyExpiresAt: string;
  toastTitle: string;
}

const initialState = {
  copyFromEntryStepId: '',
  copyPasteKey: '',
  copyPasteKeyExpiresAt: '',
  toastTitle: '',
} as const satisfies CopyPasteState;

export default function useCopyPasteSteps() {
  const dispatch = useAppDispatch();
  const [copyPasteSteps, setCopyPasteSteps] = useLocalStorage<CopyPasteState>(
    'copyPasteSteps',
    initialState,
  );

  useEffect(() => {
    if (copyPasteSteps.toastTitle) {
      dispatch(
        setGlobalCopyToastOptions({
          title: copyPasteSteps.toastTitle,
        }),
      );
    }
  }, [copyPasteSteps.toastTitle, dispatch]);

  const resetCopyPasteSteps = useCallback(() => {
    dispatch(setGlobalCopyToastOptions(null));
    setCopyPasteSteps(initialState);
  }, [dispatch, setCopyPasteSteps]);

  const clearToastTitle = useCallback(() => {
    setCopyPasteSteps(prev => ({ ...prev, toastTitle: '' }));
  }, [setCopyPasteSteps]);

  const setCopyPasteStepsAndShowToast = useCallback(
    (newCopyPasteSteps: CopyPasteState) => {
      setCopyPasteSteps(newCopyPasteSteps);
      dispatch(
        setGlobalCopyToastOptions({
          title: newCopyPasteSteps.toastTitle,
        }),
      );
    },
    [setCopyPasteSteps, dispatch],
  );

  return useMemo(
    () =>
      ({
        ...copyPasteSteps,
        clearToastTitle,
        resetCopyPasteSteps,
        setCopyPasteSteps: setCopyPasteStepsAndShowToast,
      }) as const,
    [
      copyPasteSteps,
      resetCopyPasteSteps,
      clearToastTitle,
      setCopyPasteStepsAndShowToast,
    ],
  );
}
